import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { getProducts, type Product } from '../../utils/supabase-data';

export default function BoutiquePage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [priceRange, setPriceRange] = useState<string>('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizes = ['S', 'M', 'L', 'XL'];
  const colors = ['Noir', 'Blanc', 'Gris', 'Kaki', 'Vert Olive', 'Bleu Denim'];
  const priceRanges = [
    { label: 'Tous les prix', value: '' },
    { label: 'Moins de 100 DT', value: '0-100' },
    { label: '100 - 150 DT', value: '100-150' },
    { label: '150 - 200 DT', value: '150-200' },
    { label: 'Plus de 200 DT', value: '200+' }
  ];

  const filteredProducts = products.filter(product => {
    if (selectedSize && !product.sizes.includes(selectedSize)) return false;
    if (selectedColor && !product.colors.includes(selectedColor)) return false;
    if (priceRange) {
      if (priceRange === '0-100' && product.price >= 100) return false;
      if (priceRange === '100-150' && (product.price < 100 || product.price >= 150)) return false;
      if (priceRange === '150-200' && (product.price < 150 || product.price >= 200)) return false;
      if (priceRange === '200+' && product.price < 200) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setSelectedSize('');
    setSelectedColor('');
    setPriceRange('');
  };

  const activeFiltersCount = [selectedSize, selectedColor, priceRange].filter(Boolean).length;

  const FiltersContent = () => (
    <>
      {/* Tailles */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3 tracking-wide">{t('boutique.sizes')}</h3>
        <div className="grid grid-cols-4 gap-2">
          {sizes.map(size => (
            <button
              key={size}
              onClick={() => setSelectedSize(selectedSize === size ? '' : size)}
              className={`py-2 text-sm font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                selectedSize === size
                  ? 'bg-brand-pink text-white border-brand-pink'
                  : 'bg-white text-black border-gray-300 hover:border-brand-pink'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Couleurs */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3 tracking-wide">{t('boutique.colors')}</h3>
        <div className="space-y-2">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => setSelectedColor(selectedColor === color ? '' : color)}
              className={`w-full text-left px-3 py-2 text-sm transition-all cursor-pointer whitespace-nowrap ${
                selectedColor === color
                  ? 'bg-brand-pink text-white font-bold'
                  : 'bg-white text-gray-700 hover:bg-brand-pink-pale'
              }`}
            >
              {color}
            </button>
          ))}
        </div>
      </div>

      {/* Prix */}
      <div>
        <h3 className="text-sm font-bold mb-3 tracking-wide">{t('boutique.price')}</h3>
        <div className="space-y-2">
          {priceRanges.map(range => (
            <button
              key={range.value}
              onClick={() => setPriceRange(range.value)}
              className={`w-full text-left px-3 py-2 text-sm transition-all cursor-pointer whitespace-nowrap ${
                priceRange === range.value
                  ? 'bg-brand-pink text-white font-bold'
                  : 'bg-white text-gray-700 hover:bg-brand-pink-pale'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WhatsAppButton />

      {/* Mobile Filters Overlay */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold tracking-wide">{t('boutique.filters')}</h2>
              <div className="flex items-center gap-3">
                <button onClick={resetFilters} className="text-xs text-gray-500 underline cursor-pointer whitespace-nowrap">
                  {t('boutique.reset')}
                </button>
                <button onClick={() => setShowMobileFilters(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
            <div className="p-4">
              <FiltersContent />
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full bg-black text-white py-3 text-sm font-bold tracking-widest mt-4 cursor-pointer whitespace-nowrap"
              >
                Voir {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-4xl md:text-6xl font-black mb-3 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t('boutique.title')}
            </h1>
            <p className="text-gray-600 text-sm">{t('boutique.subtitle')}</p>
          </div>

          {/* Mobile Filter Bar */}
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <p className="text-sm text-gray-600">
              <strong>{filteredProducts.length}</strong> produit{filteredProducts.length > 1 ? 's' : ''}
            </p>
            <button
              onClick={() => setShowMobileFilters(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-bold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              Filtres
              {activeFiltersCount > 0 && (
                <span className="bg-brand-pink text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar - Desktop only */}
            <div className="hidden lg:block lg:w-64 flex-shrink-0">
              <div className="bg-gray-50 p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold tracking-wide">{t('boutique.filters')}</h2>
                  <button 
                    onClick={resetFilters}
                    className="text-xs text-gray-600 hover:text-black transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('boutique.reset')}
                  </button>
                </div>
                <FiltersContent />
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="text-center py-20">
                  <i className="ri-loader-4-line text-6xl text-gray-300 animate-spin mb-4"></i>
                  <p className="text-gray-600">{t('boutique.loading')}</p>
                </div>
              ) : (
                <>
                  <div className="hidden lg:flex mb-6 items-center justify-between">
                    <p className="text-sm text-gray-600">
                      <strong>{t('boutique.productsCount', { count: filteredProducts.length })}</strong>
                    </p>
                  </div>

                  {filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" data-product-shop>
                      {filteredProducts.map(product => (
                        <Link 
                          key={product.id}
                          to={`/produit/${product.id}`}
                          className="group cursor-pointer"
                        >
                          <div className="relative overflow-hidden bg-gray-100 mb-2 sm:mb-4">
                            <div className="w-full h-52 sm:h-72 md:h-96">
                              <img 
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            {product.badge && (
                              <span className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-brand-pink text-white px-2 py-0.5 sm:px-3 sm:py-1 text-xs font-bold tracking-wide">
                                {product.badge}
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-xs sm:text-sm mb-1 sm:mb-2 tracking-wide leading-tight">{product.name}</h3>
                          <p className="text-base sm:text-lg font-black text-brand-pink-dark">{product.price} DT</p>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                      <p className="text-gray-600">{t('boutique.noResults')}</p>
                      <button 
                        onClick={resetFilters}
                        className="mt-4 bg-brand-pink text-white px-8 py-3 text-sm font-bold tracking-wide hover:bg-brand-pink-dark transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t('boutique.reset')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
